// Shared hydration for editing an existing ASN (incoming request) in the
// pallet/carton form shapes both CreateRequest pages use.
//
// Invariants this module owns (RF8 review findings):
//
// 1. Declared value round-trips. Lines store $/unit
//    (declared_unit_value); the forms edit a line TOTAL. Hydration
//    multiplies back out so a no-op edit re-submits the same value
//    instead of an empty field that erases it.
//
// 2. Standalone cartons are derivable. Cartons are stored as plain
//    RequestLineItems (packagingType 'carton', not referenced by any
//    PalletItem) -- there is no Cartons collection on the API response.
//
// 3. Serial/MAC selections round-trip. Hardcoding empty arrays here
//    submitted them as structural updates that wiped stored
//    identifiers (or 400'd on lines with activity). Hydration restores
//    them from requested_serials/requested_macs, falling back to the
//    line's pending-receive Assets.
//
// 4. Packaging, weight, and dimensions round-trip. The destructive
//    admin update recreates lines from the form state and requires
//    weight > 0 -- hydrating without these fields made a no-op edit of
//    an existing pallet fail (or silently flip carton packaging back
//    to individual).
//
// Each hydrated item carries requestLineItemId so the customer PATCH
// path can update the existing line instead of creating an unlinked
// duplicate. The admin path (delete + recreate) simply ignores it.

const dimsFromColumns = (source, prefix) => ({
  length: source[`${prefix}_length`] || '',
  width: source[`${prefix}_width`] || '',
  height: source[`${prefix}_height`] || '',
  weight: source[`${prefix}_weight`] || ''
});

// Stored $/unit -> form line total, currency-rounded.
export const lineTotalDeclaredValue = (line, quantity) => {
  const perUnit = parseFloat(line?.declared_unit_value);
  if (!Number.isFinite(perUnit) || perUnit <= 0 || !(quantity > 0)) return '';
  return Math.round(perUnit * quantity * 100) / 100;
};

// The nested PalletItems->RequestLineItem rows are attribute-whitelisted
// on the admin endpoint; the top-level RequestLineItems collection has
// the full row (dims, weight, serial selections, assets). Enrich nested
// lines from it by id.
const lineDetailsById = (existingRequest) => new Map(
  (existingRequest?.RequestLineItems || []).map((line) => [line.id, line])
);

// Only PENDING_RECEIVE assets are editable identifiers -- a partially
// received line also carries received/shipped asset rows, and exposing
// those as requested serials would submit a structural change the
// patch rightly rejects.
const pendingAssets = (line) => (line.Assets || [])
  .filter((asset) => asset.status === 'PENDING_RECEIVE');

const serialsFromLine = (line) => {
  if (Array.isArray(line.requested_serials) && line.requested_serials.length) {
    return line.requested_serials;
  }
  return pendingAssets(line).map((asset) => asset.serialNumber).filter(Boolean);
};

const macsFromLine = (line) => {
  if (Array.isArray(line.requested_macs) && line.requested_macs.length) {
    return line.requested_macs;
  }
  return pendingAssets(line).map((asset) => asset.macAddress).filter(Boolean);
};

const hydrateLineShape = (line, quantity) => ({
  requestLineItemId: line.id || null,
  productSku: line.productSku || '',
  productName: line.productName || '',
  quantity,
  unitOfMeasure: line.unitOfMeasure || 'each',
  packagingType: line.packagingType || line.packaging_type || 'individual',
  cartonCount: line.cartonCount || line.carton_count || null,
  unitsPerCarton: line.units_per_carton || null,
  weight: line.weight || '',
  cartonDimensions: dimsFromColumns(line, 'carton'),
  itemDimensions: dimsFromColumns(line, 'item'),
  individualDimensions: dimsFromColumns(line, 'item'),
  dimensions: line.dimensions || null,
  serialNumbers: serialsFromLine(line),
  macAddresses: macsFromLine(line),
  declaredValue: lineTotalDeclaredValue(line, quantity),
  // Operational metadata the destructive admin edit would otherwise
  // recreate as null/false (the normalizer persists all of these).
  // Project fields hydrate under the CAMELCASE aliases the form inputs
  // edit -- supplying snake_case here left the inputs blank while the
  // stale stored value out-priorified the operator's edit in the
  // normalizer (item.project_name || item.projectName).
  projectName: line.project_name || '',
  projectCode: line.project_code || '',
  specialHandling: line.specialHandling || null,
  storageRequirement: line.storageRequirement || null,
  isHazardous: line.isHazardous === true,
  requested_asset_ids: Array.isArray(line.requested_asset_ids) && line.requested_asset_ids.length
    ? line.requested_asset_ids
    : null,
  notes: line.notes || ''
});

// Line ids already represented on a pallet -- everything else with
// packagingType 'carton' is a standalone carton.
const palletLinkedLineIds = (existingRequest) => {
  const ids = new Set();
  (existingRequest?.Pallets || []).forEach((pallet) => {
    (pallet.PalletItems || []).forEach((palletItem) => {
      const lineId = palletItem.RequestLineItem?.id || palletItem.requestlineitem_id;
      if (lineId) ids.add(lineId);
    });
  });
  return ids;
};

export const hydrateCartonsFromRequest = (existingRequest) => {
  const linkedIds = palletLinkedLineIds(existingRequest);
  return (existingRequest?.RequestLineItems || [])
    .filter((line) => (
      (line.packagingType || line.packaging_type) === 'carton' && !linkedIds.has(line.id)
    ))
    .map((line) => ({
      ...hydrateLineShape(line, line.quantity),
      internalSku: line.InventoryItem?.internal_sku || '',
      cartonCount: line.cartonCount || line.carton_count || 1,
      unitsPerCarton: line.units_per_carton || 1,
      packagingType: 'carton',
      // Consolidated dimensions JSON -- the admin recreate path reads
      // cartonData.dimensions.weight for the line weight.
      dimensions: line.dimensions || dimsFromColumns(line, 'carton')
    }));
};

export const hydratePalletsFromRequest = (existingRequest) => {
  const detailsById = lineDetailsById(existingRequest);

  return (existingRequest?.Pallets || []).map((pallet) => {
    const items = (pallet.PalletItems || []).map((palletItem) => {
      const nestedLine = palletItem.RequestLineItem || {};
      const line = detailsById.get(nestedLine.id) || nestedLine;
      const quantity = palletItem.quantity_on_pallet || line.quantity || 1;
      return hydrateLineShape(line, quantity);
    });

    // The stored type wins: a multi-SKU pallet currently holding one
    // item must stay multi, or an unrelated edit persists a
    // classification change. Item count is only the legacy fallback --
    // except that >1 items can never render in the single editor
    // (which would silently drop the extras on submit).
    const storedType = pallet.type === 'multi' || pallet.type === 'single' ? pallet.type : null;
    const type = items.length > 1 ? 'multi' : (storedType || 'single');
    const single = type === 'single' && items.length === 1 ? items[0] : null;

    return {
      id: pallet.id,
      label: pallet.pallet_label || pallet.label || '',
      // Single-SKU pallets edit through the pallet-level fields (type
      // drives which editor and which submit shape is used); their line
      // fields are mirrored to the pallet level below.
      type,
      // Pallet dimensions live in length/width/height columns -- there
      // is no dimensions JSON column on the model.
      dimensions: {
        length: pallet.length || pallet.dimensions?.length || '',
        width: pallet.width || pallet.dimensions?.width || '',
        height: pallet.height || pallet.dimensions?.height || ''
      },
      weight: pallet.weight_lbs || pallet.weight || '',
      notes: pallet.notes || '',
      depalletizeOnArrival: pallet.depalletize_on_arrival || false,
      crossDock: pallet.cross_dock || false,
      declaredValue: single ? single.declaredValue : '',
      ...(single ? {
        requestLineItemId: single.requestLineItemId,
        productSku: single.productSku,
        productName: single.productName,
        quantity: single.quantity,
        unitOfMeasure: single.unitOfMeasure,
        packagingType: single.packagingType,
        cartonCount: single.cartonCount || 1,
        unitsPerCarton: single.unitsPerCarton || 1,
        cartonDimensions: single.cartonDimensions,
        itemDimensions: single.itemDimensions,
        serialNumbers: single.serialNumbers,
        macAddresses: single.macAddresses,
        projectName: single.projectName,
        projectCode: single.projectCode,
        specialHandling: single.specialHandling,
        storageRequirement: single.storageRequirement,
        isHazardous: single.isHazardous,
        requested_asset_ids: single.requested_asset_ids
      } : {}),
      items
    };
  });
};
